import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import { Button, Icon, Dropdown } from '@vmos2/design-system';
import { ClickOutside, useLocale, useRoute } from '@vmos2/utils';
import { useConfig, useDeviceSize, useSettings, useUser } from '@vmos2/providers';

type Props = {
  withoutBorder?: boolean,
  isOnLanding?: boolean,
};

export const LocaleSwitcher = ({ withoutBorder, isOnLanding }: Props) => {
  const config = useConfig();
  const settings = useSettings();
  const { isMobile } = useDeviceSize();
  const { isLocaleSwitcherEnabled, locale, locales, changeLocale } = useLocale(config, settings);
  const { navigateTo, route } = useRoute();
  const [isOpen, setOpenStatus] = useState(false);
  const { isLoggedIn, updateUserLocale } = useUser();

  const handleClick = useCallback(
    selectedCode => {
      navigateTo(route.name, { ...route.params, locale: selectedCode });
      setOpenStatus(false);
      changeLocale(selectedCode);

      if (isLoggedIn) {
        updateUserLocale(selectedCode);
      }
    },
    [changeLocale, isLoggedIn, navigateTo, route, updateUserLocale]
  );

  const selectedLocale = find(locales, ({ code }) => toLower(code) === toLower(locale));

  if (!isLocaleSwitcherEnabled || isEmpty(locales)) {
    return null;
  }

  const sortedLocales = locales.sort((x, y) =>
    toLower(x.code) === toLower(locale) ? -1 : toLower(y.code) === toLower(locale) ? 1 : 0
  );

  return (
    <ClickOutside onClickOutside={() => setOpenStatus(false)}>
      <Container withoutBorder={withoutBorder}>
        <Switcher>
          <LocaleSwitcherBtn
            iconLeft={<Icon name="Language" size={18} />}
            iconRight={!isMobile && <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={12} />}
            size="small"
            mode="ghost"
            color="dark"
            dataTest="locale-switcher-btn"
            onClick={() => setOpenStatus(!isOpen)}
            hasDropdownArrow={isOpen}
            isOnLanding={isOnLanding}
          >
            {!isMobile && get(selectedLocale, 'name')}
          </LocaleSwitcherBtn>

          {isOpen && (
            <Options minWidth={113} maxHeight="261px" data-test="locale-list" withoutBorder={withoutBorder}>
              {sortedLocales.map(({ code, name }) => (
                <Option key={code} selected={toLower(code) === toLower(locale)} onClick={() => handleClick(code)}>
                  {name}
                </Option>
              ))}
            </Options>
          )}
        </Switcher>
      </Container>
    </ClickOutside>
  );
};

const Container = styled('div')`
  display: flex;
  align-items: center;

  &:after {
    position: relative;
    display: ${({ withoutBorder }) => (withoutBorder ? 'none' : 'block')};
    content: '';
    width: 1px;
    height: 24px;
    margin: 0 16px;
    background-color: ${({ theme }) => theme.color.lightGrey};
  }

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    &:after {
      margin: 0 8px;
    }
  }
`;

const Switcher = styled('div')`
  position: relative;
`;

const LocaleSwitcherBtn = styled(Button)`
  background-color: ${({ theme, isOnLanding }) => isOnLanding && theme.color.white};
  :hover {
    background-color: ${({ theme, isOnLanding }) => isOnLanding && theme.color.white};
  }
  :focus {
    box-shadow: none;
  }

  & span:first-of-type {
    margin-right: ${({ theme }) => theme.margins.xs};
  }

  & span:last-of-type {
    margin-left: ${({ theme }) => theme.margins.xs};
  }

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    padding: 10px 3px;
    border-radius: ${({ theme }) => theme.borderRadius.ellipse};
  }
`;

const Options = styled(Dropdown)`
  align-items: flex-start;
  padding: 8px 0;
  color: ${({ theme }) => theme.color.black};

  @media (${({ theme }) => theme.breakpoint.tablet}) {
    padding: 8px 0;
    margin-right: 0;
  }

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    width: auto;
    top: 37px;
  }

  ${({ withoutBorder }) =>
    withoutBorder &&
    `
    left: inherit;
    right: 0;
    transform: none;
  `}
`;

const Option = styled('div')`
  font-size: ${({ theme }) => theme.fontSize.md};
  text-align: left;
  white-space: nowrap;
  width: 100%;
  padding: 8px 16px;
  cursor: pointer;

  ${({ theme, selected }) =>
    selected &&
    `
    font-weight: ${theme.fontWeight.bold};
    background-color: ${theme.color.lighterGrey};
  `}

  &:last-of-type {
    margin-bottom: 0;
  }
`;
